import { useEffect } from "react"

import { App } from "antd"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import { Navigate, useLocation } from "react-router-dom"

import Button from "../../Components/Button"
import Spinner from "../../Components/Spinner"
import { RoutesPaths } from "../../constants/user-routes"
import Icon from "../../Icon"
import { isOnboardingFinished } from "../../Utils/onboarding/isOnboardingFinished"
import { hasSelectedFeature } from "../../Utils/onboarding/selectedFeaturesStorage"
import RecoverEmptyState from "./components/EmptyState"
import { activateRecover, getProfile } from "./pages/ConfigurePage/redux/configure.thunks"
import { getDisputesStatistics } from "./redux/disputes.thunks"

import "./styles.less"

const DeactivatedBanner = ({ onReactivate }) => {
  const { t } = useTranslation()
  const { modal } = App.useApp()
  // Reactivation is deliberate: the button opens a confirm popup first, so a stray
  // click on the banner can't silently turn automation back on.
  const handleReactivate = () => {
    modal.confirm({
      title: t("Reactivate Recover?"),
      okText: t("Reactivate"),
      cancelText: t("Cancel"),
      centered: true,
      content: t(
        "Recover will resume fighting new disputes automatically, using your saved profile and Preferences."
      ),
      onOk: onReactivate
    })
  }
  // Reuse the house in-page warning banner design (.limited-banner from
  // Components/Banners): orange-bordered, themed via tokens, action on the right.
  // The wrapper just aligns it to the page's 32px gutter.
  return (
    <div className="recover-deactivated-banner">
      <div className="limited-banner">
        <Icon type="alert_triangle_error" style={{ fontSize: 20 }} />
        <span className="text">
          {t("Recover is deactivated. New chargebacks aren't being fought and you're at risk.")}
        </span>
        <Button onClick={handleReactivate} text={t("Reactivate")} />
      </div>
    </div>
  )
}

// Route shell for /recover/*. Activation gates the feature in two ways:
//   - Activated: pages render normally.
//   - Deactivated WITH disputes on file: keep showing the page (All / details /
//     Analytics / Preferences) but warn with a reactivate banner. Preferences
//     has its own "Activate Recover first" empty state (PreferencesPage/index.js),
//     so it doesn't need a route-level bounce here.
//   - Not activated WITHOUT any disputes: show the activation empty state. This is every
//     account before setup is finished, including one still in onboarding or in demo mode:
//     Recover has no demo data, so an empty table there is a defect, not a demo.
const DisputesShell = ({
  children,
  isActivated,
  everActivated,
  fetching,
  profileLoaded,
  loadFailed,
  statistics,
  statisticsLoaded,
  fetchProfile,
  fetchStatistics,
  activate,
  userInfo
}) => {
  const location = useLocation()

  useEffect(() => {
    fetchProfile()
    fetchStatistics()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const spinner = (
    <div
      style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}
    >
      <Spinner />
    </div>
  )

  // While signup is unfinished, Configure OPENS but cannot be walked: a merchant with no
  // connected processor cannot activate Recover, so letting them fill later steps would build
  // a setup they can never finish. Any step past the first bounces back to Integrate, which
  // leaves the wizard fully viewable while going nowhere. Everything else under Recover stays
  // reachable and shows its normal not-activated states. Guarded on userInfo so a hydrating
  // store cannot bounce an onboarded merchant.
  const configurePath = location?.pathname || ""
  const isBeyondFirstConfigureStep =
    configurePath.startsWith(`${RoutesPaths.recover.configure}/`) &&
    !configurePath.startsWith(RoutesPaths.recover.configure_section("integrations"))
  if (userInfo && !isOnboardingFinished(userInfo) && isBeyondFirstConfigureStep)
    return <Navigate to={RoutesPaths.recover.configure_section("integrations")} replace />

  // Wait for the first profile fetch before deciding anything; otherwise a direct
  // load sees the initial (not-activated) state and wrongly redirects/empties.
  if (fetching || !profileLoaded) return spinner
  // Demo mode is deliberately not a bypass here: a fresh account is "demo" until its first
  // deposit, and letting it through showed an empty dispute table instead of the way in.
  if (isActivated) return children
  // A failed profile fetch (5xx/401/timeout) is NOT the same as a deactivated
  // profile: isActivated is still at its initial false, so showing the deactivated
  // branch here would falsely tell an activated merchant "Recover is deactivated,
  // you're at risk". Keep the spinner so the next successful fetch resolves the
  // real state instead of flashing a wrong, alarming banner.
  if (loadFailed) return spinner

  // --- Deactivated ---
  const pathname = location?.pathname || ""
  // The Configure wizard is the activation path (Finish & save activates Recover,
  // disabled until a gateway is connected), so let a not-yet-activated merchant in.
  // Preferences is likewise always reachable so every setting is visible in any
  // state; the page shows the Configure nudge banner itself when setup isn't done.
  if (
    pathname.startsWith(RoutesPaths.recover.configure) ||
    pathname.startsWith(RoutesPaths.recover.preferences)
  )
    return children
  // Wait for the dispute count before choosing empty-vs-banner, so we don't flash
  // the wrong state.
  if (!statisticsLoaded) return spinner
  const hasDisputes = (statistics?.all || 0) > 0
  if (!hasDisputes) return <RecoverEmptyState />
  // A merchant who just finished onboarding with Recover selected but never activated
  // gets the Configure-focused RecoverConfigureBanner instead (rendered by AllPage), so
  // this quick-reactivate banner doesn't stack on top of it. A merchant who DID activate
  // and later deactivated should get this reactivate banner rather than the "finish
  // setup" one, so the ever-activated case is excluded. `everActivated` comes from the
  // backend now (RecoverProfile.first_activated_at); it used to be a localStorage marker,
  // which meant a device change made a set-up merchant look brand new.
  const showConfigureBannerInstead =
    isOnboardingFinished(userInfo) && hasSelectedFeature("recover") && !everActivated
  return (
    <>
      {!showConfigureBannerInstead && <DeactivatedBanner onReactivate={activate} />}
      {children}
    </>
  )
}

const mapStateToProps = (state) => ({
  isActivated: state.disputesConfigure.profile.isActivated,
  everActivated: state.disputesConfigure.profile.everActivated,
  fetching: state.disputesConfigure.profile.fetching,
  profileLoaded: state.disputesConfigure.profile.profileLoaded,
  loadFailed: state.disputesConfigure.profile.loadFailed,
  statistics: state.disputes.list.statistics,
  statisticsLoaded: state.disputes.list.statisticsLoaded,
  userInfo: state.auth?.userInfo
})

const mapDispatchToProps = (dispatch) => ({
  fetchProfile: () => dispatch(getProfile()),
  fetchStatistics: () => dispatch(getDisputesStatistics()),
  activate: () => dispatch(activateRecover())
})

export default connect(mapStateToProps, mapDispatchToProps)(DisputesShell)
